/**
 * Text on a Path — the `textOnPathElement` wire field, both directions (WMPCM-9542).
 *
 * A text-on-a-path is **not** an element of its own in this editor: it always rides on a host
 * object's outline, so there is no geometry it could carry that the host does not already own.
 * NSD models it the same way — `<TP>` is a child of `<PE>`, a sibling of `<RR>`/`<IP>`/`<RP>`/
 * `<PL>`/`<OV>`, not a page element of its own — which is why `XmlPageElement` declares both.
 * So `buildTextOnPathElement` is spread onto the host's StowedElement **alongside** its own
 * `rectangleElement`/`irregularPolygonElement`/… and the host keeps its own `kindId`.
 *
 * Do NOT "promote" a text-on-path host to `kindId: 6`. `StowedElement.GetUnderlyingElement()`
 * (Go, `larry-common-library/src/models/document_models/stowed_element.go`) dispatches on `kindId` alone, so
 * `kindId: 6` makes the TEXT the element that gets serialised and the host shape is the thing that
 * is dropped — the outline the text runs along would cease to exist.
 *
 * What does NOT round trip, and why this is not the place to fix it:
 *  - `colorHex` — `FillStyle` addresses colours by palette `colorId`, and no client-side
 *    hex→colorId mapping exists yet anywhere in this frontend (see `SHAPE_DEFAULT_FILL_COLOR_ID`
 *    in `stowed-element-builder.ts`, which hardcodes one for the same reason). The run's
 *    `fillStyle` is written with the default black (`colorId` 2) rather than a guess at the hex.
 *  - `startSegment` (`SS`) — `TextOnPathConfig.startDistance` is a single distance measured along
 *    the whole path, because that is what `getPointAtLength` and `<textPath startOffset>` take.
 *    Legacy splits the same quantity into a segment index plus a distance into that segment; with
 *    `SS` pinned to 0, `SP` carries the whole distance, which is the same position.
 *
 * **Backend schema skew.** `larry-common-library/src/models/document_models/text_on_path_element.go` went
 * through two shapes before landing on the one documented above: originally a flat
 * `Characters []*TextOnPathCharacter` field (`json:"characters"`) directly on the element with no
 * `TextLine` at all, then wrapped in `CharacterContainer` (commit `7b3c9419`), then gained
 * `TextLine` (commit `494f5a36`). A live environment can still be running a build that predates
 * one or both of those — confirmed against a real `checkpoints/get` response carrying a flat,
 * top-level `characters` field and no `textLine` key whatsoever. Both read functions below accept
 * either shape, and `buildTextOnPathElement` writes both (the flat field is inert extra JSON to a
 * backend new enough to have `characterContainer`, and `textLine` is inert extra JSON to one old
 * enough not to — Go's `encoding/json` silently ignores unmapped keys either direction), so
 * neither end of this file needs another edit once the backend redeploys. What CANNOT be
 * papered over from here: a build old enough to lack `TextLine` entirely has nowhere to put the
 * string on its own struct, so `readTextOnPathConfig` falls back to reconstructing it by
 * concatenating each baked character's own `character` field when `textLine` yields nothing —
 * recovering the text from geometry that survives on either backend shape, rather than losing it
 * outright until that backend is redeployed.
 */

import type { TextOnPathElementData, TextRun } from '@/api/types/stowed-element'
import { SHAPE_DEFAULT_FILL_COLOR_ID } from '@/lib/elements/constants/shape-default-fill-color-id'
import { measureTextOnPathCharacters } from '@/lib/shapes/text-on-path-characters'
import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import { createTextOnPathConfig } from '@/lib/shapes/text-on-path-session'
import type { BuildTextOnPathElementArgs } from './types/build-text-on-path-element-args'

/** Wire field spellings a decoder may see: camelCase (REST JSON), snake_case and Go PascalCase. */
const TEXT_ON_PATH_FIELDS = [
  'textOnPathElement',
  'text_on_path_element',
  'TextOnPathElement',
] as const

/** Full-strength tint for the run's `fillStyle` (a shape's own default fill uses a light tint). */
const TEXT_ON_PATH_DEFAULT_FILL_TINT = 100

type AnyRecord = Record<string, unknown>

function asRecord(value: unknown): AnyRecord | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as AnyRecord)
    : null
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function booleanish(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()

    if (v === 'true') return true
    if (v === 'false') return false
  }

  return null
}

/**
 * Reads the per-character array from either backend shape (see "Backend schema skew" above):
 * nested `characterContainer.characters` (current) or a flat top-level `characters` (pre-
 * `7b3c9419`). Nested wins when both are present, since a current build is the one this frontend
 * writes and a stale flat mirror should never shadow it.
 */
function pickCharacters(record: AnyRecord): AnyRecord[] {
  const container =
    asRecord(record.characterContainer) ??
    asRecord(record.character_container) ??
    asRecord(record.CharacterContainer)
  const nested = container ? (asArray(container.characters) ?? asArray(container.Characters)) : null
  const flat = asArray(record.characters) ?? asArray(record.Characters)
  const list = nested ?? flat ?? []

  return list.map(asRecord).filter((entry): entry is AnyRecord => entry != null)
}

/**
 * Reconstructs the string from baked `<PX>` entries, in array order, for a backend build old
 * enough to have shipped `characters` with no `textLine` at all (see "Backend schema skew"
 * above). Each `TextOnPathCharacter` carries its own glyph (`character`), so this recovers exactly
 * the same string `<TL>` would have held — degraded only in that the per-run typography `<TL>`
 * would have carried (font, size, colour, tracking, kerning) is unrecoverable and falls back to
 * `createTextOnPathConfig()`'s defaults, same as any other field the wire has no value for.
 */
function textFromCharacters(characters: AnyRecord[]): string {
  return characters.map((c) => (typeof c.character === 'string' ? c.character : '')).join('')
}

/**
 * `TextOnPathElementData` for a committed config.
 *
 * `anchor` (`AN`) carries the direction flip: `TextOnPathConfig.clockwise` is the one field the
 * session model and the wire agree on by name (see the field map at the top of
 * `text-on-path-session.ts`).
 *
 * The single `TextRun` is deliberate rather than a limitation of the writer — `TextOnPathConfig`
 * holds one uniform style over the whole string (`text` is a plain `string` with no run model), so
 * there is nothing finer to split on. `lineStyle` is the empty object the API contract shows;
 * `fillStyle` carries the default black, see the module comment for why it is not populated from
 * `colorHex`.
 */
export function buildTextOnPathElement({
  config,
  base,
  characters,
}: BuildTextOnPathElementArgs): TextOnPathElementData {
  const textRun: TextRun = {
    textContent: config.text,
    fontFamilyId: config.fontFamilyId,
    fontId: config.fontId,
    pointSize: config.fontSizePt,
    kerning: config.kerning,
    tracking: config.tracking,
    lineStyle: {},
    fillStyle: {
      colorId: SHAPE_DEFAULT_FILL_COLOR_ID,
      tint: TEXT_ON_PATH_DEFAULT_FILL_TINT,
    },
  }
  const resolvedCharacters =
    characters ??
    measureTextOnPathCharacters({ text: config.text, source: null, width: 0, height: 0 })

  return {
    base,
    margin: 0,
    distance: config.startDistance,
    startSegment: 0,
    offset: config.offset,
    anchor: config.clockwise,
    textLine: { textRuns: [textRun] },
    characterContainer: { characters: resolvedCharacters },
    // Mirrors `characterContainer.characters` under the pre-`7b3c9419` flat field name — see
    // `TextOnPathElementData.characters` and this module's "Backend schema skew" for why.
    characters: resolvedCharacters,
  }
}

/** Pulls the `textOnPathElement` payload out of a checkpoint row or a decoded element, if present. */
export function pickTextOnPathElementData(...sources: unknown[]): AnyRecord | null {
  for (const source of sources) {
    const record = asRecord(source)

    if (!record) continue

    for (const field of TEXT_ON_PATH_FIELDS) {
      const nested = asRecord(record[field])

      if (nested) return nested
    }
  }

  return null
}

/**
 * Rebuilds a `TextOnPathConfig` from the wire, or `undefined` when the element carries no text.
 *
 * Merged over `createTextOnPathConfig()` rather than assembled field by field, so a field the wire
 * has no home for (`colorHex`) — or one added to the config later — arrives at its default instead
 * of `undefined`, which would render its toolbar control empty with nothing to enumerate from.
 * That is the same defence `createTextOnPathDraft` already applies to a stored config.
 *
 * The text comes from `<TL>`, never from `<XT>`: the character container is the baked half and may
 * legitimately hold zeroed entries (see `measureTextOnPathCharacters`), while `<TL>` is the logical
 * string. Runs are concatenated, since a writer other than this one (yb360) may split the same
 * string across several `<TR>`s.
 */
export function readTextOnPathConfig(data: unknown): TextOnPathConfig | undefined {
  const record = asRecord(data)

  if (!record) return undefined

  const textLine =
    asRecord(record.textLine) ?? asRecord(record.text_line) ?? asRecord(record.TextLine)
  const runs = textLine
    ? (asArray(textLine.textRuns) ??
      asArray(textLine.TextRuns) ??
      asArray(textLine.text_runs) ??
      [])
    : []

  let text = ''
  let firstRun: AnyRecord | null = null

  for (const entry of runs) {
    const run = asRecord(entry)

    if (!run) continue
    if (firstRun == null) firstRun = run

    const content = run.textContent ?? run.TextContent ?? run.text_content

    if (typeof content === 'string') text += content
  }

  // `<TL>` is absent entirely on a backend build old enough to predate commit `494f5a36` (see the
  // module comment's "Backend schema skew") — there is no `textLine` field on its Go struct for
  // any JSON to have landed in. Recover the string from the baked characters instead, rather than
  // reporting "no text on this path" for an object that plainly has some.
  if (text.trim().length === 0) {
    text = textFromCharacters(pickCharacters(record))
  }

  if (text.trim().length === 0) return undefined

  const defaults = createTextOnPathConfig()
  const distance = finiteNumber(record.distance ?? record.Distance)
  const offset = finiteNumber(record.offset ?? record.Offset)
  const anchor = booleanish(record.anchor ?? record.Anchor)

  const fontFamilyId = finiteNumber(firstRun?.fontFamilyId ?? firstRun?.font_family_id)
  const fontId = finiteNumber(firstRun?.fontId ?? firstRun?.font_id)
  const pointSize = finiteNumber(firstRun?.pointSize ?? firstRun?.point_size)
  const kerning = finiteNumber(firstRun?.kerning ?? firstRun?.Kerning)
  const tracking = finiteNumber(firstRun?.tracking ?? firstRun?.Tracking)

  return {
    ...defaults,
    text,
    startDistance: distance ?? defaults.startDistance,
    offset: offset ?? defaults.offset,
    clockwise: anchor ?? defaults.clockwise,
    fontFamilyId: fontFamilyId ?? defaults.fontFamilyId,
    fontId: fontId ?? defaults.fontId,
    // A point size of 0 is not a size — treat it the way an absent field is treated.
    fontSizePt: pointSize != null && pointSize > 0 ? pointSize : defaults.fontSizePt,
    kerning: kerning ?? defaults.kerning,
    tracking: tracking ?? defaults.tracking,
  }
}

/** Convenience for the two read paths: find the payload in either source, then decode it. */
export function readTextOnPathConfigFrom(...sources: unknown[]): TextOnPathConfig | undefined {
  return readTextOnPathConfig(pickTextOnPathElementData(...sources))
}
